"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Users,
  KeyRound,
  Copy,
  Check,
  Rocket,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { motion } from "framer-motion";
import { useStore } from "@/lib/store";
import type { Workspace } from "@/lib/types";

/**
 * Shown in the main view when a workspace is empty (no teams, no issues).
 * Guides the user through the 3-step setup: add team → spawn agent → wire
 * Claude Code. Dismisses automatically once they have ≥1 team and ≥1 issue.
 */
export function WorkspaceQuickstart({
  workspace,
  onAddClient,
  onOpenAgents,
}: {
  workspace: Workspace;
  onAddClient: () => void;
  onOpenAgents: () => void;
}) {
  // Count teams for this workspace without returning a new array from the
  // selector — Zustand+useSyncExternalStore treats an unstable selector
  // output as a snapshot mismatch and throws React #185 (infinite render).
  // We only need the count here anyway.
  const teamsCount = useStore(
    (s) => s.teams.filter((t) => t.workspaceId === workspace.id).length
  );
  const teams = useMemo(
    () => ({ length: teamsCount }),
    [teamsCount]
  );
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  function copy(key: string, text: string) {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  }

  const mcpSnippet = useMemo(
    () =>
      JSON.stringify(
        {
          mcpServers: {
            [`pauta-${workspace.slug}`]: {
              type: "streamable-http",
              url:
                typeof window !== "undefined"
                  ? `${window.location.origin}/api/mcp`
                  : "https://pauta.dev/api/mcp",
              headers: {
                Authorization: "Bearer pta_your_token_from_step_2",
              },
            },
          },
        },
        null,
        2
      ),
    [workspace.slug]
  );

  const bootstrapPrompt = `Bootstrap. Then follow the operating manual.`;

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <div
            className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center text-white text-[26px] shadow-lg"
            style={{
              background: `linear-gradient(135deg, ${workspace.color}, color-mix(in srgb, ${workspace.color} 55%, #000))`,
            }}
          >
            {workspace.icon}
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight mb-1">
            {workspace.name} is ready
          </h1>
          <p className="text-[13px] text-[var(--fg-tertiary)]">
            {workspace.category === "dev"
              ? "Two steps to wire an agent to this dev workspace."
              : "Three steps to put an agent on this workspace."}
          </p>
        </motion.div>

        {/* Dev workspaces auto-create a default team — no "add team" step. */}
        {workspace.category !== "dev" && (
          <Step
            n={1}
            icon={<Users size={14} />}
            title="Add your first team"
            body={
              teams.length === 0
                ? "Teams are functional groupings inside this workspace (data, ml, ops, marketing…). Each gets its own task prefix (like DATA-1, ML-7, OPS-12)."
                : `You have ${teams.length} team${teams.length === 1 ? "" : "s"} already. You can add more any time.`
            }
            done={teams.length > 0}
            action={
              <button
                onClick={onAddClient}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[12.5px] font-medium"
              >
                <Users size={12} />
                {teams.length === 0 ? "Add first team" : "Add another team"}
              </button>
            }
            delay={0.05}
          />
        )}

        <Step
          n={workspace.category === "dev" ? 1 : 2}
          icon={<KeyRound size={14} />}
          title="Generate an agent token"
          body="A bot user scoped to this workspace. Any writes by the agent are attributed to this bot and show up with a 🤖 badge in the Activity feed. Token shown once."
          action={
            <button
              onClick={onOpenAgents}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--bg-tertiary)] border border-[var(--border-strong)] hover:bg-[var(--bg-hover)] text-[12.5px] font-medium"
            >
              <KeyRound size={12} />
              Open Agents &amp; tokens
            </button>
          }
          delay={0.1}
        />

        <Step
          n={workspace.category === "dev" ? 2 : 3}
          icon={<Rocket size={14} />}
          title="Wire Claude Code"
          body={
            <>
              Paste this into your <code>~/.claude.json</code>, restart Claude Code,
              then start a session with the minimal prompt below.
            </>
          }
          action={
            <div className="space-y-2 mt-2 w-full">
              <div className="relative">
                <div className="absolute top-2 right-2 z-10">
                  <button
                    onClick={() => copy("mcp", mcpSnippet)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[var(--bg-hover)] border border-[var(--border)] hover:border-[var(--border-strong)] text-[10.5px]"
                  >
                    {copiedKey === "mcp" ? (
                      <>
                        <Check size={10} /> Copied
                      </>
                    ) : (
                      <>
                        <Copy size={10} /> Copy
                      </>
                    )}
                  </button>
                </div>
                <pre className="text-[11px] font-mono bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md p-3 overflow-x-auto text-[var(--fg-secondary)] max-h-[200px]">
                  {mcpSnippet}
                </pre>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-wide text-[var(--fg-muted)] font-medium mb-1">
                  Then your first message is just
                </div>
                <div className="relative">
                  <div className="absolute top-2 right-2 z-10">
                    <button
                      onClick={() => copy("prompt", bootstrapPrompt)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[var(--bg-hover)] border border-[var(--border)] hover:border-[var(--border-strong)] text-[10.5px]"
                    >
                      {copiedKey === "prompt" ? (
                        <>
                          <Check size={10} /> Copied
                        </>
                      ) : (
                        <>
                          <Copy size={10} /> Copy
                        </>
                      )}
                    </button>
                  </div>
                  <pre className="text-[12px] font-mono bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md p-3 text-[var(--fg)]">
                    {bootstrapPrompt}
                  </pre>
                </div>
                <p className="text-[10.5px] text-[var(--fg-muted)] mt-1.5 flex items-center gap-1">
                  <Sparkles size={10} />
                  The agent reads the operating manual automatically via{" "}
                  <code>get_workspace_guide</code>.
                </p>
              </div>
            </div>
          }
          delay={0.15}
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="mt-6 text-center text-[11px] text-[var(--fg-muted)]"
        >
          <span className="inline-flex items-center gap-1">
            Once you&apos;ve added tasks, this panel disappears.
            <ArrowRight size={10} />
          </span>
        </motion.div>
      </div>
    </div>
  );
}

function Step({
  n,
  icon,
  title,
  body,
  action,
  done,
  delay = 0,
}: {
  n: number;
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  action: React.ReactNode;
  done?: boolean;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="rounded-xl border border-[var(--border)] bg-[var(--bg-secondary)] p-4 mb-3"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0"
          style={{
            background: done ? "var(--green)" : "var(--bg-tertiary)",
            color: done ? "white" : "var(--fg-secondary)",
            border: done ? "none" : "1px solid var(--border-strong)",
          }}
        >
          {done ? <Check size={12} /> : n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[var(--fg-tertiary)]">{icon}</span>
            <h3 className="text-[13.5px] font-semibold">{title}</h3>
          </div>
          <div className="text-[12.5px] text-[var(--fg-secondary)] leading-relaxed mb-3">
            {body}
          </div>
          {action}
        </div>
      </div>
    </motion.div>
  );
}
